/**
 * Fibonacci spiral layout for @xyflow/react KG visualization.
 *
 * Golden angle positioning prevents node overlap with organic distribution.
 * Optionally groups nodes by label so same-type nodes cluster together.
 */

import type { Node, Edge } from '@xyflow/react'
import type { KgBrowserNode, KgBrowserRelationship } from '@/lib/api'
import { KG_NODE_STYLES, KG_RELATIONSHIP_COLORS, DEFAULT_NODE_STYLE, DEFAULT_RELATIONSHIP_COLOR } from './kg-constants'

const GOLDEN_ANGLE = 2.399963 // radians ≈ 137.508°
const BASE_SPACING = 60

export interface SpiralLayoutOptions {
  groupByLabel?: boolean
  spacing?: number
  selectedNodeIds?: Set<string>
}

export interface KgFlowNodeData {
  kgId: string
  label: string
  caption: string
  color: string
  size: number
  properties: Record<string, unknown>
  selected: boolean
  [key: string]: unknown
}

export interface KgFlowEdgeData {
  relType: string
  color: string
  [key: string]: unknown
}

export function computeSpiralLayout(
  kgNodes: KgBrowserNode[],
  kgRels: KgBrowserRelationship[],
  options?: SpiralLayoutOptions,
): { nodes: Node<KgFlowNodeData>[]; edges: Edge<KgFlowEdgeData>[] } {
  const spacing = options?.spacing ?? BASE_SPACING
  const selectedIds = options?.selectedNodeIds ?? new Set<string>()

  // Optionally sort by label to cluster same-type nodes
  const ordered = options?.groupByLabel
    ? [...kgNodes].sort((a, b) => a.label.localeCompare(b.label))
    : kgNodes

  const nodes: Node<KgFlowNodeData>[] = ordered.map((n, i) => {
    const angle = i * GOLDEN_ANGLE
    const radius = spacing * Math.sqrt(i + 1)
    const style = KG_NODE_STYLES[n.label] || DEFAULT_NODE_STYLE

    return {
      id: n.id,
      type: 'kgNode',
      position: {
        x: Math.cos(angle) * radius,
        y: Math.sin(angle) * radius,
      },
      data: {
        kgId: n.id,
        label: n.label,
        caption: n.caption,
        color: style.color,
        size: style.size,
        properties: n.properties,
        selected: selectedIds.has(n.id),
      },
    }
  })

  const edges: Edge<KgFlowEdgeData>[] = kgRels.map(r => ({
    id: r.id,
    source: r.from,
    target: r.to,
    type: 'kgEdge',
    data: {
      relType: r.type,
      color: KG_RELATIONSHIP_COLORS[r.type] || DEFAULT_RELATIONSHIP_COLOR,
    },
  }))

  return { nodes, edges }
}
